import { updateComplianceConsents } from '@/api/account';
import { toast } from '@/components/Toast';
import { getStoreInstance } from '@/store';
import logger from '@/utils/client-logger';

/** POST:/api/compliance/consents 返回体中的 data 字段 */
export type ComplianceConsentsResponseData = {
  success?: boolean;
  message?: string;
};

export interface ComplianceConsentExtra {
  email?: string;
  tel_code?: string;
  telephone?: string;
}

export interface UpdateConsentsOptions {
  /** 场景码，与 global init 返回的 compliance_policies 的 key 一致，如 web.login、web.register */
  sceneCode: string;
  extra?: ComplianceConsentExtra;
  /** 页面中文名（埋点/合规页标识），如 route 元信息中的 cnName */
  page?: string;
  status?: number;
  consentTypes?: number[];
  metadata?: Record<string, unknown>;
  showResponseToast?: boolean;
}

type CompliancePolicyRow = {
  consent_type: number;
  id?: string | number;
  policy_id?: string | number;
  policy_version: string | number;
};

type ComplianceScene = {
  policies?: CompliancePolicyRow[];
};

/**
 * 登录/注册成功后：根据 global init 下发的 compliance_policies 自动上报协议同意；
 * 若接口 data.success 不为 false，会调用 store 的 getMarketingStatus 刷新 complianceMarketingStatus；
 * 若 success 为 false 且携带 message，会以 toast 提示（与 vooglam updateConsents 行为对齐）。
 * @returns 无需上报或上报通过时为 true；接口明确返回 data.success === false 时为 false（调用方可中止后续流程）。
 * 请求异常会先打日志再抛出，由调用方决定如何提示用户。
 */
export async function updateConsentsAfterAuth(options: UpdateConsentsOptions): Promise<boolean> {
  try {
    const { sceneCode, extra, page, consentTypes, status, metadata, showResponseToast } = options;
    const store = getStoreInstance();
    const compliancePolicies = store?.getState().compliancePolicies;
    const scene = compliancePolicies?.[sceneCode] as ComplianceScene | undefined;
    const policies = scene?.policies || [];
    if (!policies.length) return true;

    const targetPolicies =
      consentTypes && consentTypes.length
        ? policies.filter((policy) => consentTypes.includes(policy.consent_type))
        : policies;
    if (!targetPolicies.length) return true;

    const items = targetPolicies.map((policy) => {
      const policyId = policy.id ?? policy.policy_id;
      const item: {
        consent_type: number;
        policy_id: string | number;
        policy_version: string | number;
        metadata?: Record<string, unknown>;
        email?: string;
        tel_code?: string;
        telephone?: string;
        status?: number;
      } = {
        consent_type: policy.consent_type,
        policy_id: policyId as string | number,
        policy_version: policy.policy_version,
      };
      if (status !== undefined) {
        item.status = status;
      }
      if (metadata) {
        item.metadata = metadata;
      }
      if (policy.consent_type === 3 && extra?.email) {
        item.email = extra.email;
      }
      if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.tel_code) {
        item.tel_code = extra.tel_code;
      }
      if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.telephone) {
        item.telephone = extra.telephone;
      }
      return item;
    });

    const res = await updateComplianceConsents({
      scene_code: sceneCode,
      page: page ?? '',
      items,
    });

    const consentData = res?.data as ComplianceConsentsResponseData | undefined;
    const responseMessage = consentData?.message || res?.msg;

    if (showResponseToast && responseMessage) {
      if (res?.code === 200) {
        toast.success(responseMessage);
      } else {
        toast.error(responseMessage);
      }
    } else if (consentData?.success === false && responseMessage) {
      if (items.length > 1) {
        await store?.getState().getMarketingStatus();
      }
      toast.info(responseMessage);
    }

    if (consentData?.success !== false) {
      await store?.getState().getMarketingStatus();
      return true;
    }
    return false;
  } catch (error) {
    logger.error(error, `useComplianceConsent: updateConsentsAfterAuth ${options.sceneCode}`);
    throw error;
  }
}
export function useComplianceConsent() {
  return { updateConsentsAfterAuth };
}
